/**
 * ProfileDropdown - Hover dropdown menu for user profile options
 * 
 * Shows user info and options on hover:
 * - User name and email
 * - Notifications Settings
 * - Edit My Profile
 * - Sign Out
 */

import React, { useState, useRef, useEffect } from 'react';

const ProfileDropdown = ({ user, onLogout, onEditProfile, onNotificationSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Get user initials for avatar
  const getInitials = () => {
    const name = getDisplayName();
    if (name && name !== '...') {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      const emailName = user.email.split('@')[0];
      return emailName.substring(0, 2).toUpperCase();
    }
    return '...';
  };

  // Get display name - use display_name from database
  const getDisplayName = () => {
    if (user?.display_name) return user.display_name;
    if (user?.name) return user.name;
    if (user?.displayName) return user.displayName;
    return user ? 'User' : '...';
  };

  // Handle mouse enter - open dropdown
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  // Handle mouse leave - close with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const menuItems = [
    {
      id: 'notifications',
      label: 'Notifications Settings',
      icon: '🔔',
      onClick: onNotificationSettings
    },
    {
      id: 'profile',
      label: 'Edit My Profile',
      icon: '✏️',
      onClick: onEditProfile
    }
  ];

  const handleItemClick = (item) => {
    setIsOpen(false);
    if (item.onClick) {
      item.onClick();
    }
  };

  const handleSignOut = () => {
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  // Button size to match other header buttons (44px)
  const buttonSize = '44px';

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-tutorial="profile-dropdown"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Trigger Button - Same style as other header buttons with initials */}
      <button
        style={{
          width: buttonSize,
          height: buttonSize,
          padding: 0,
          background: 'transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          fontSize: '0.85rem',
          fontWeight: '600',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
          e.currentTarget.style.borderColor = '#dc3545';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title={getDisplayName()}
      >
        {getInitials()}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            minWidth: '280px',
            overflow: 'hidden',
            zIndex: 10000,
            animation: 'profileDropdownFadeIn 0.2s ease',
          }}
        >
          {/* User Info Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
          }}>
            <div style={{ 
              fontWeight: '600', 
              fontSize: '1rem', 
              color: '#111827',
              marginBottom: '4px'
            }}>
              {getDisplayName()}
            </div>
            <div style={{ 
              fontSize: '0.85rem', 
              color: '#6b7280' 
            }}>
              {user?.email || user?.username || ''}
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px' }}>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease',
                  color: '#374151',
                  fontSize: '0.9rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span style={{ fontSize: '1.1rem', width: '24px' }}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* Sign Out Button */}
          <div style={{ 
            padding: '8px', 
            borderTop: '1px solid #e5e7eb' 
          }}>
            <button
              onClick={handleSignOut}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.9rem',
                color: '#374151',
                fontWeight: '500',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
                e.currentTarget.style.borderColor = '#fecaca';
                e.currentTarget.style.color = '#dc2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#374151';
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes profileDropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileDropdown;
